import { validate, version } from "uuid";

import { EnumsObject } from "commons/types/api/enums";
import { FilterOperation, FilterType } from "commons/types/table";

import { AttributesType, HtmlType } from "commons/ui/HtmlWrapper";

import { hasAll, hasOneOf } from "commons/utils/conditionChecker";
import numeral from "commons/utils/numeral";

import { check } from "../commons-fetch/utils/utils";

export * from "../commons-fetch/utils/utils";

export const valueOrEmpty = (value: any): any => {
    return value ?? " - ";
};

export const join = (separator: string, ...args: any[]): string => {
    const arrayToUse = [];

    for (const arg of args) {
        if (arg) {
            arrayToUse.push(arg);
        }
    }

    return arrayToUse.join(separator);
};

export const checkUserAuthorities = (
    userAuthorities: Array<string>,
    neededAuthorities: Array<string>,
    oneOf?: boolean
): boolean => {
    if (check(userAuthorities) && Array.isArray(userAuthorities)) {
        if (oneOf) {
            return hasOneOf(userAuthorities, neededAuthorities);
        } else {
            return hasAll(userAuthorities, neededAuthorities);
        }
    } else {
        return false;
    }
};

export const filterWithNotEmptyArray = (
    filtersToCheck: Array<FilterType>,
    key: string,
    filterOperation?: FilterOperation
): boolean => {
    let filterOperationToFind = FilterOperation.In;

    if (filterOperation) {
        filterOperationToFind = filterOperation;
    }

    const filterFind = filtersToCheck.find(
        (filterToCheck) =>
            filterToCheck.key === key && filterToCheck.operation === filterOperationToFind
    );

    if (filterFind && Array.isArray(filterFind.value) && filterFind.value.length > 0) {
        return true;
    } else {
        return false;
    }
};

export const filterWithMandatoryValue = (
    filtersToCheck: Array<FilterType>,
    key: string,
    filterOperation: FilterOperation
): boolean => {
    const filterFind = filtersToCheck.find(
        (filterToCheck) => filterToCheck.key === key && filterToCheck.operation === filterOperation
    );

    if (
        filterFind &&
        filterFind.value !== null &&
        filterFind.value !== undefined &&
        filterFind.value !== ""
    ) {
        return true;
    } else {
        return false;
    }
};

const setContent = (node: any): Array<HtmlType | string> => {
    const content: Array<HtmlType | string> = [];

    if (node.childNodes != null && node.childNodes.length) {
        node.childNodes.forEach((item: any) => {
            if (item.nodeType === 3) {
                content.push(item.nodeValue);
            } else {
                const child = htmlTree(item);

                if (child !== undefined) {
                    content.push(child);
                }
            }
        });
    }

    return content;
};

const setAttributes = (node: any): AttributesType => {
    const attributes: AttributesType = {};

    if (node.attributes != null && node.attributes.length) {
        Array.from(node.attributes).forEach((item: any) => {
            attributes[item.nodeName] = item.nodeValue;
        });
    }

    return attributes;
};

const htmlTree = (node: any): HtmlType | undefined => {
    if (node) {
        return {
            type: node.nodeName,
            content: setContent(node),
            attributes: setAttributes(node),
        };
    }

    return undefined;
};

export const mapDOM = (htmlString: string): HtmlType | undefined => {
    // If string convert to document Node
    if (typeof htmlString === "string") {
        if (window.DOMParser) {
            const parser = new DOMParser();

            const docNode = parser.parseFromString(
                `<div>${cleanHtmlBreakLine(htmlString)}</div>`,
                "text/xml"
            );

            return htmlTree(docNode.firstChild);
        }
    }

    return undefined;
};

export const cleanHtmlBreakLine = (htmlString: string) => {
    return htmlString.replace(/<br>/g, "<br />");
};

export const cleanHtml = (htmlString: string) => {
    return htmlString
        .replace(/&/g, "&amp;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
};

export const mapItalicTags = (label: string): string => {
    return label.replace(/&lt;i&gt;/, "<i>").replace(/&lt;\/i&gt;/, "</i>");
};

export const removeItalicTags = (label: string): string => {
    return label.replace(/<i>/, "").replace(/<\/i>/, "");
};

const reverseHtml = (str: string) => {
    const ph = String.fromCharCode(206);
    let result = str.split("").reverse().join("");

    while (result.indexOf("<") > -1) {
        result = result.replace("<", ph);
    }

    while (result.indexOf(">") > -1) {
        result = result.replace(">", "<");
    }

    while (result.indexOf(ph) > -1) {
        result = result.replace(ph, ">");
    }

    return result;
};

export const truncateHTML = (htmlString: string, length: number): string => {
    if (htmlString.length <= length) {
        return htmlString;
    }

    let truncated = `${htmlString.substring(0, length)}...`;

    // Remove line breaks and surrounding whitespace
    truncated = truncated.replace(/(\r\n|\n|\r)/gm, "").trim();

    // If the text ends with an incomplete start tag, trim it off
    truncated = truncated.replace(/<(\w*)(?:(?:\s\w+(?:={0,1}(["']{0,1})\w*\2{0,1})))*$/g, "");

    // If the text ends with a truncated end tag, fix it.
    const truncatedEndTagExpr = /<\/((?:\w*))...$/g;
    const truncatedEndTagMatch = truncatedEndTagExpr.exec(truncated);

    if (truncatedEndTagMatch != null) {
        const truncatedEndTag = truncatedEndTagMatch[1];

        // Check to see if there's an identifiable tag in the end tag
        if (truncatedEndTag.length > 0) {
            // If so, find the start tag, and close it
            const startTagExpr = new RegExp(
                "<(" + truncatedEndTag + "\\w?)(?:(?:\\s\\w+(?:=([\"'])\\w*\\2)))*>"
            );

            let testString = truncated;
            let startTagMatch = startTagExpr.exec(testString);

            let startTag = null;
            while (startTagMatch != null) {
                startTag = startTagMatch[1];
                testString = testString.replace(startTagExpr, "");
                startTagMatch = startTagExpr.exec(testString);
            }

            if (startTag != null) {
                truncated = truncated.replace(truncatedEndTagExpr, "...</" + startTag + ">");
            }
        } else {
            // Otherwise, cull off the broken end tag
            truncated = truncated.replace(truncatedEndTagExpr, "...");
        }
    }

    // Now the tricky part. Reverse the text, and look for opening tags. For each opening tag,
    //  check to see that he closing tag before it is for that tag. If not, append a closing tag.
    let testStringReversed = reverseHtml(truncated);
    const reverseTagOpenExpr = /<(?:(["'])\w*\1=\w+ )*(\w*)>/;
    let tagMatch = reverseTagOpenExpr.exec(testStringReversed);

    while (tagMatch != null) {
        const tag = tagMatch[0];
        const tagName = tagMatch[2];
        const startPos = tagMatch.index;
        const endPos = startPos + tag.length;
        const fragment = testStringReversed.substring(0, endPos);

        // Test to see if an end tag is found in the fragment. If not, append one to the end
        //  of the truncated HTML, thus closing the last unclosed tag
        if (!new RegExp("<" + tagName + "/>").test(fragment)) {
            truncated += "</" + reverseHtml(tagName) + ">";
        }

        // Get rid of the already tested fragment
        testStringReversed = testStringReversed.replace(fragment, "");

        // Get another tag to test
        tagMatch = reverseTagOpenExpr.exec(testStringReversed);
    }

    return truncated;
};

export const scrollToTop = () => {
    window.scrollTo({
        top: 0,
        behavior: "smooth",
    });
};

export const scrollToBottom = () => {
    window.scrollTo({
        top: document.documentElement.scrollHeight,
        behavior: "smooth",
    });
};

export const stringCapitalize = (str: string): string => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const getFullAddressFormatted = (
    address: string | null,
    place: string | null,
    postalCode: string | null,
    city: string | null,
    province: string | null
): string => {
    let label = "";

    if (address) {
        label += `${address}`;
    }

    if (place) {
        label += ` presso ${place}`;
    }

    if ((postalCode || city || province) && address) {
        label += ",";
    }

    if (postalCode) {
        label += ` ${postalCode}`;
    }

    if (city) {
        label += ` ${city}`;
    }

    if (province) {
        label += ` (${province})`;
    }

    return label ? label : "-";
};

export const resolveEmptyString = (value: string | null): string | null => {
    if (value) {
        return value;
    }

    return null;
};

export const formatNumbersWithDecimal = (value: number): string => {
    return numeral(value).format("0,0[.]0[0000000]");
};

export const checkValueInEnum = (enums: EnumsObject, value: string, notIn?: boolean): boolean => {
    let isPresent = false;

    enums.forEach((item) => {
        if (item.value === value) {
            isPresent = true;
        }
    });

    return notIn ? !isPresent : isPresent;
};

export const isValidUUIDv4 = (uuid: string): boolean => {
    return validate(uuid) && version(uuid) === 4;
};
